import React, { useRef, useState, useEffect } from "react";
import { createPortal } from 'react-dom';
import { useMediaQuery } from 'react-responsive'
import config from '../config/environment';

import "./styles/containers.css";
import "./styles/images.css";
import "./styles/text.css";
import './styles/button.css';
import './styles/animations.css';

import { toLowerCasePath } from '../extensions/utilityExtensions';

const API_URL = config.API_URL;

function SearchPageComponent() {
    const isMobileLarge = useMediaQuery({ query: '(height > 1616px)' });
    const isMobileWidth = useMediaQuery({ maxWidth: 1024 });
    const inputRef = useRef(null);
    const buttonRef = useRef(null);
    const dropdownRef = useRef(null);
    const containerRef = useRef(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [active, setActive] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Example suggestions - replace with your actual suggestions logic
    const [suggestions, setSuggestions] = useState([
        "test", "asdasd", "tes", "blender", "bea", "asasdas"
    ]);
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);

    // Filter suggestions based on input
    useEffect(() => {
        if (searchQuery.length > 0) {
            const filtered = suggestions.filter(
                suggestion => suggestion.toLowerCase().indexOf(searchQuery.toLowerCase()) > -1
            );
            setFilteredSuggestions(filtered);
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    }, [searchQuery, suggestions]);

    // Close suggestions and search bar when clicking outside
    useEffect(() => {
        function handleClickOutside(event) {
            const target = event.target;
            const isOutsideContainer = !containerRef.current || !containerRef.current.contains(target);
            const isOutsideButton = !buttonRef.current || !buttonRef.current.contains(target);

            if (isOutsideContainer && isOutsideButton) {
                setShowSuggestions(false);
                setActive(false);
            }
        }

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const onEnterKeyDownEvent = (e) => {
        if (e.key === 'Enter') {
            window.location.href = toLowerCasePath(`/search/${searchQuery}`);
        }
    }

    const handleMouseEnter = () => {
        if (!isMobileWidth) {
            setActive(true);
        }
    };

    const handleMouseLeave = () => {
        if (!isMobileWidth && document.activeElement !== inputRef.current && searchQuery.length === 0) {
            setActive(false);
            setShowSuggestions(false);
        }
    };

    const handleFocus = () => {
        setActive(true);
        if (searchQuery.length > 0) {
            setShowSuggestions(true);
        }
    };

    const handleBlur = (e) => {
        // Only deactivate if focus is not moving to another element within our component
        // When using Portal, inputRef is not inside buttonRef, so we check explicit refs
        const related = e.relatedTarget;
        const isInternal =
            buttonRef.current?.contains(related) ||
            dropdownRef.current?.contains(related) ||
            inputRef.current?.contains(related) ||
            inputRef.current === related;

        if (!isInternal && !isMobileWidth) {
            // On desktop, blur closes it (unless hovering, which is handled by mouseleave)
            // But we might want to keep it open if there is text? 
            // Current behavior: Hover opens. Click keeps open (focus).
            // User wants Toggle.
            setActive(false);
        }
        // On mobile, blur usually hides keyboard. We might want to keep the bar open until explicit close?
        // But user asked for Toggle button. So usually click button to open, click button to close.
        // If I click 'Done' on keyboard, focus is lost. 
        // If we want it to stay open, we skip setActive(false) here for mobile.
    };

    const handleButtonClick = (e) => {
        // Prevent immediate closing if clicking the button while focused
        // Toggle logic:
        if (active) {
            // If clicking the button (icon) while active, we want to Close.
            // But if we are just clicking input to focus, we don't close.
            // The button wraps everything.
            // If target is the Icon or the container (not input), we toggle.
            if (e.target !== inputRef.current) {
                setActive(false);
                setShowSuggestions(false);
            }
        } else {
            setActive(true);
            setTimeout(() => {
                inputRef.current?.focus();
            }, 50);
        }
    };

    const handleSuggestionClick = (suggestion) => {
        setSearchQuery(suggestion);
        setShowSuggestions(false);
        inputRef.current?.focus();
    };

    const handleClearSearch = () => {
        setSearchQuery("");
        inputRef.current?.focus();
    };

    const renderSearchInput = () => (
        <div ref={containerRef} className={`search-input-container ${active ? 'active' : ''}`}>
            <input
                ref={inputRef}
                className="searchInputContainer"
                placeholder="Search a Blog"
                value={searchQuery}
                onKeyDown={onEnterKeyDownEvent}
                onChange={e => setSearchQuery(e.target.value)}
                type="text"
                id="searchQuery"
                name="searchQuery"
                onBlur={handleBlur}
                onFocus={handleFocus}
                autoComplete="off"
            />
            {searchQuery && (
                <span
                    className="clear-search"
                    onClick={handleClearSearch}
                >
                    ×
                </span>
            )}

            {showSuggestions && filteredSuggestions.length > 0 && (
                <div className="suggestions-dropdown" ref={dropdownRef}>
                    {filteredSuggestions.map((suggestion, index) => (
                        <div
                            key={index}
                            className="suggestion-item"
                            onMouseDown={() => handleSuggestionClick(suggestion)}
                        >
                            {suggestion}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <button
            ref={buttonRef}
            className="buttonEmpty search-button"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleButtonClick}
            type="button"
        >
            <div className="searchContainer">
                {isMobileWidth ? createPortal(renderSearchInput(), document.body) : renderSearchInput()}

                <img
                    className={`${isMobileLarge ? "image48px" : "image24px"} ${active ? 'search-icon-active' : ''}`}
                    src="../images/search.png"
                    alt="Search Icon"
                />
            </div>
        </button>
    );
}

export default SearchPageComponent;